// Plan 08-08 — mode-aware tween config (easing + duration tokens).
// Plan 30-01 — Runespring pulse durations added.
// Plan 32-01 — reveal-cascade + anchor lifecycle durations added.
//
// Per UI-SPEC §"Reduced-motion substitution table":
//   ease-bouncy → ease-snap-out (same duration; flatten the spring overshoot)
//   ease-cinematic → ease-snap-out (200ms linear-ish — no big swooshes)
//   dur-cinematic-long  (2500ms) → dur-quick (200ms)  — reveals collapse to single fade
//   dur-cinematic-short (800ms)  → dur-quick (200ms)  — power-up overlays compress
//   dur-spawn / dur-anchor-consume / dur-anchor-place → dur-quick (200ms)
//     — Phase 32 cinematic transitions all collapse to a 200ms cross-fade.
//
// Non-substituted tokens stay canonical. Reduced motion is NOT a global
// time-warp — micro-interactions (button compress, list-row settle, modal
// dismissal) still run at their declared duration so the UI stays crisp.
//
// Tween-library binding: this module returns the *config* values; the
// caller (PixiJS tween / @tweenjs/tween.js / hand-rolled) reads the spring
// stiffness/damping for spring tokens or the cubic-bezier coefficient array
// for ease tokens. Per RESEARCH §"Open Question 1" we use `tweedle.js`
// (already a dep — `tweedle.js@2.1.0`) because it integrates cleanly with
// PixiJS Ticker without spinning a second RAF loop.
//
// Pitfall 5 lock: NEVER set `app.ticker.speed = 0` to "pause" — gate at
// THIS layer (substitute the curve / shorten the duration). See
// ticker-gate.ts for the no-op rationale.

export type EasingToken =
  | 'ease-snap-in'
  | 'ease-snap-out'
  | 'ease-bouncy'
  | 'ease-pop'
  | 'ease-cinematic'

export type DurationToken =
  | 'dur-instant'
  | 'dur-quick'
  | 'dur-default'
  | 'dur-generous'
  | 'dur-cinematic-short'
  | 'dur-cinematic-long'
  // Plan 30-01 — Runespring pulse cycles. UI-SPEC §4 Open Tokens.
  // Both suspend under reduced motion (selectDuration → 0; sprite
  // renders at static peak alpha — see UI-SPEC §5 fallback table).
  | 'dur-pulse-mythic'
  | 'dur-pulse-anchor'
  // Plan 32-01 — flop/river reveal cascade + LetterCubeSprite anchor
  // lifecycle tokens. UI-SPEC §2 + §4 §6 state-machine durations.
  //   dur-spawn          (200ms): per-cell spawn-in rise + alpha when a new
  //     cell appears (flop / river 12-cell cascade — caller stages 12 ×
  //     100ms stagger over `dur-spawn` lifetime). Idle. ease-bouncy.
  //   dur-anchor-consume (400ms): Anchor → Hollow transition (stone-fall
  //     emit).
  //   dur-anchor-place   (600ms): new-Anchor stone-carve sequence —
  //     placement + Claim. Longer than consume because the brass-spark +
  //     rune-glyph ghost overlay needs settle time after the brief carve.
  // All three suspend under reduced motion → 200ms cross-fade per UI-SPEC §8.
  | 'dur-spawn'
  | 'dur-anchor-consume'
  | 'dur-anchor-place'
  // Plan/Phase 4 — board center-grow box + cube re-anchor (500ms; RM -> 0 snap).
  | 'dur-board-grow'

export type CubicBezier = readonly [number, number, number, number]

export interface CubicBezierEasing {
  readonly type: 'cubic-bezier'
  readonly value: CubicBezier
}

export interface SpringEasing {
  readonly type: 'spring'
  readonly stiffness: number
  readonly damping: number
}

export type EasingConfig = CubicBezierEasing | SpringEasing

// Locked verbatim from UI-SPEC §"Easing curves".
export const EASING: Readonly<Record<EasingToken, EasingConfig>> = {
  'ease-snap-in': {
    type: 'cubic-bezier',
    value: [0.4, 0.0, 0.2, 1.0],
  },
  'ease-snap-out': {
    type: 'cubic-bezier',
    value: [0.0, 0.0, 0.2, 1.0],
  },
  'ease-bouncy': {
    type: 'spring',
    stiffness: 350,
    damping: 22,
  },
  'ease-pop': {
    type: 'spring',
    stiffness: 500,
    damping: 18,
  },
  'ease-cinematic': {
    type: 'cubic-bezier',
    value: [0.45, 0.0, 0.05, 1.0],
  },
}

// Locked verbatim from UI-SPEC §"Duration tokens".
export const DURATION_MS: Readonly<Record<DurationToken, number>> = {
  'dur-instant': 80,
  'dur-quick': 200,
  'dur-default': 300,
  'dur-generous': 400,
  'dur-cinematic-short': 800,
  'dur-cinematic-long': 2500,
  // Plan 30-01 — pulse cycle durations (UI-SPEC §4 Open Tokens).
  'dur-pulse-mythic': 1400,
  'dur-pulse-anchor': 2400,
  // Plan 32-01 — reveal-cascade + anchor lifecycle durations (UI-SPEC §4 + §6).
  'dur-spawn': 200,
  'dur-anchor-consume': 400,
  'dur-anchor-place': 600,
  // Plan/Phase 4 — board center-grow box + cube re-anchor (500ms; RM -> 0 snap).
  'dur-board-grow': 500,
}

/**
 * Plan 30-01 — cubic-bezier approximation of the spring-based `ease-bouncy`
 * for tweedle.js callers (tweedle.js does NOT ship a spring solver). Per
 * Open Q2 recommendation A (locked in CONTEXT.md), `[0.34, 1.56, 0.64, 1.0]`
 * is the canonical bouncy curve — control point y > 1 produces the
 * characteristic overshoot then settle behavior.
 *
 * Callers that DO have a spring solver (e.g. future Pixi+spring lib) should
 * read `EASING['ease-bouncy']` instead — the spring config is the canonical
 * representation. The cubic-bezier is the lossy-but-usable fallback.
 */
export const EASE_BOUNCY_CUBIC_BEZIER = [0.34, 1.56, 0.64, 1.0] as const

/**
 * Plan 30-01 — parametric cubic-bezier easing solver.
 *
 * Returns a function `f(t)` such that for t ∈ [0,1] we get the y-coordinate
 * of the cubic-bezier curve P0=(0,0), P1=(p1x,p1y), P2=(p2x,p2y),
 * P3=(1,1) at the parameter where x(s) = t. Implementation: find `s` via
 * Newton-Raphson (4 iterations is usually enough on monotonic curves; fall
 * back to bisection for curves with x'(s) ≈ 0). Then compute y(s).
 *
 * This is the same algorithm browsers ship for the CSS `cubic-bezier()`
 * timing function. Hot-path: tweens evaluate this O(60) times per second
 * per active tween — 4 Newton iterations + a final Bernstein evaluation
 * is well under 1µs in V8.
 *
 * Boundary handling: f(0) === 0 and f(1) === 1 exactly (no float jitter at
 * tween start/end). Out-of-range inputs clamp to [0, 1].
 */
export function cubicBezierEasing(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
): (t: number) => number {
  // Bernstein-form cubic-bezier coordinate functions for P0=(0,0)
  // P3=(1,1). x(s) = 3(1-s)^2 s p1x + 3(1-s) s^2 p2x + s^3
  //          y(s) = 3(1-s)^2 s p1y + 3(1-s) s^2 p2y + s^3
  // Derivative: x'(s) = 3(1-s)^2 p1x + 6(1-s)s(p2x - p1x) + 3s^2(1 - p2x)
  const sampleX = (s: number): number => {
    const inv = 1 - s
    return 3 * inv * inv * s * p1x + 3 * inv * s * s * p2x + s * s * s
  }
  const sampleY = (s: number): number => {
    const inv = 1 - s
    return 3 * inv * inv * s * p1y + 3 * inv * s * s * p2y + s * s * s
  }
  const sampleDerivativeX = (s: number): number => {
    const inv = 1 - s
    return (
      3 * inv * inv * p1x + 6 * inv * s * (p2x - p1x) + 3 * s * s * (1 - p2x)
    )
  }

  const solveFor = (targetX: number): number => {
    // Initial guess: targetX itself (works well for near-linear curves).
    let s = targetX
    for (let i = 0; i < 8; i++) {
      const x = sampleX(s) - targetX
      const dx = sampleDerivativeX(s)
      if (Math.abs(x) < 1e-6) return s
      if (Math.abs(dx) < 1e-6) break // dead zone — bail to bisection
      s = s - x / dx
    }
    // Bisection fallback (cubic-bezier x is monotonic when p1x,p2x ∈ [0,1]).
    let lo = 0
    let hi = 1
    s = targetX
    for (let i = 0; i < 32; i++) {
      const x = sampleX(s)
      if (Math.abs(x - targetX) < 1e-6) return s
      if (x < targetX) lo = s
      else hi = s
      s = (lo + hi) / 2
    }
    return s
  }

  return (t: number): number => {
    if (t <= 0) return 0
    if (t >= 1) return 1
    return sampleY(solveFor(t))
  }
}

/**
 * Pick the easing curve for a token, substituting per UI-SPEC §"Reduced-motion
 * substitution table" when `reduced` is true. Identity for non-substituted
 * tokens.
 */
export function selectEasing(
  token: EasingToken,
  reduced: boolean,
): EasingConfig {
  if (reduced && token === 'ease-bouncy') return EASING['ease-snap-out']
  if (reduced && token === 'ease-cinematic') return EASING['ease-snap-out']
  return EASING[token]
}

/**
 * Pick the duration in ms for a token, substituting per UI-SPEC §"Reduced-
 * motion substitution table" when `reduced` is true. Identity for non-
 * substituted tokens (micro-interactions stay at their declared duration).
 *
 * Plan 30-01: the two Runespring pulse tokens (`dur-pulse-mythic` and
 * `dur-pulse-anchor`) both substitute to `0` under reduced motion — the
 * caller short-circuits the sine-wave alpha tween entirely and renders
 * the sprite at static peak alpha (UI-SPEC §5 fallback table).
 */
export function selectDuration(token: DurationToken, reduced: boolean): number {
  if (reduced && token === 'dur-cinematic-long') return DURATION_MS['dur-quick']
  if (reduced && token === 'dur-cinematic-short')
    return DURATION_MS['dur-quick']
  // Plan 30-01 — suspend pulse cycles under reduced motion.
  if (reduced && token === 'dur-pulse-mythic') return 0
  if (reduced && token === 'dur-pulse-anchor') return 0
  // Plan 32-01 — reveal-cascade + anchor lifecycle tokens collapse to a
  // 200ms cross-fade under reduced motion (UI-SPEC §8 substitution table).
  // dur-spawn already = 200ms canonical — included for explicitness so
  // future re-tuning of the canonical value cannot accidentally lengthen
  // reduced-motion cell spawn-in.
  if (reduced && token === 'dur-spawn') return DURATION_MS['dur-quick']
  if (reduced && token === 'dur-anchor-consume') return DURATION_MS['dur-quick']
  if (reduced && token === 'dur-anchor-place') return DURATION_MS['dur-quick']
  if (reduced && token === 'dur-board-grow') return 0
  return DURATION_MS[token]
}

/**
 * Convenience aggregate for callers that need both at once (the common
 * pattern is "pick easing+duration for this animation token pair").
 */
export const TWEEN_CONFIG = {
  EASING,
  DURATION_MS,
  selectEasing,
  selectDuration,
} as const
